import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Collapse,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
import { convertDateToWords } from "../../utils/dateToWords";

const TERMS = [
  { key: "first Term", label: "Term-I" },
  { key: "Second Term", label: "Term-II" },
  { key: "Final Term", label: "Final Exam" },
];

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");
};

// Student Info Fields Configuration
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true },
  rollNo: { label: "Roll No", defaultVisible: true },
  name: { label: "Student's Name", defaultVisible: true },
  father: { label: "Father's Name", defaultVisible: true },
  mother: { label: "Mother's Name", defaultVisible: true },
  apaarId: { label: "Apaar ID", defaultVisible: true },
  aadhaarNumber: { label: "Aadhaar Number", defaultVisible: false },
  studied: { label: "Studied (Class)", defaultVisible: true },
  admissionNo: { label: "Admission No.", defaultVisible: true },
  standard: { label: "Standard", defaultVisible: false },
  casteCategory: { label: "Category/Caste", defaultVisible: false },
  dob: { label: "Date of Birth", defaultVisible: true },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false },
  heightWeight: { label: "Height/Weight", defaultVisible: false },
  contact: { label: "Contact Number", defaultVisible: false },
  address: { label: "Address", defaultVisible: false },
};

// Renders the full 2-page block for ONE student.
function StudentTwoPageMarksheet({
  studentData,
  school,
  activities,
  totalGrade,
  totalActivity,
  terms,
  isLast,
  themeColor,
  selectedTerm,
  isLandscape,
  fieldVisibility,
}) {
  // Debug: Log the student data to see structure
  // console.log("Student Data:", studentData);

  // Extract marksheets - handle both possible structures
  let marksheets = [];
  let profile = {};

  if (studentData) {
    // If studentData has marksheets array directly
    if (studentData.marksheets && Array.isArray(studentData.marksheets)) {
      marksheets = studentData.marksheets;
      profile = studentData;
    }
    // If studentData is the marksheet itself (flat structure)
    else if (studentData.examType) {
      marksheets = [studentData];
      profile = studentData;
    }
    // If studentData has data array with marksheets
    else if (studentData.data && Array.isArray(studentData.data)) {
      marksheets = studentData.data;
      profile = studentData.data[0] || {};
    }
    // If studentData has records array
    else if (studentData.records && Array.isArray(studentData.records)) {
      marksheets = studentData.records;
      profile = studentData.records[0] || {};
    }
    // Fallback - use studentData as is
    else {
      profile = studentData;
      // Try to find marksheets in any property
      for (const key in studentData) {
        if (Array.isArray(studentData[key]) && studentData[key].length > 0 && studentData[key][0]?.examType) {
          marksheets = studentData[key];
          break;
        }
      }
    }
  }

  // console.log("Marksheets found:", marksheets);
  // console.log("Profile:", profile);

  // Get classroom info from the first marksheet
  const classroomInfo = marksheets[0]?.classRoom || profile?.classRoom || {};

  // Get all subjects from marksheets
  const subjects = useMemo(() => {
    const allMarks = marksheets.flatMap((ms) => ms.marks || []);
    // console.log("All marks:", allMarks);
    const unique = [
      ...new Map(allMarks.map((m) => [m.subjectName?.trim(), m])).values(),
    ];
    return unique.sort((a, b) =>
      a.subjectName?.toLowerCase().localeCompare(b.subjectName?.toLowerCase())
    );
  }, [marksheets]);

  // console.log("Subjects:", subjects);

  const findTerm = (examType) => {
    const found = marksheets.find((m) =>
      m.examType?.trim().toLowerCase().includes(examType?.trim().toLowerCase())
    );
    // console.log(`Finding term ${examType}:`, found);
    return found;
  };

  const findMark = (examType, subject) => {
    const term = findTerm(examType);
    return term?.marks?.find(
      (s) => s.subjectName?.trim().toLowerCase() === subject?.trim().toLowerCase()
    );
  };

  const getTotals = (examType, rem) => {
    const term = findTerm(examType);
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks
      .filter((s) => s.remark !== rem)
      .reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks
      .filter((s) => s.remark !== rem)
      .reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  const getTotalsNoon = (examType, rem) => {
    const term = findTerm(examType);
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks
      .filter((s) => s.remark === rem)
      .reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks
      .filter((s) => s.remark === rem)
      .reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  // Filter visible terms
  const visibleTerms =
    selectedTerm === "all"
      ? TERMS
      : TERMS.filter((t) => t.key.toLowerCase() === selectedTerm.toLowerCase());

  const academicTotals = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotals(t.key, "Non");
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marksheets]);

  const nonAcademicTotals = useMemo(() => {
    const map = {};
    TERMS.forEach((t) => {
      map[t.key] = getTotalsNoon(t.key, "Non");
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marksheets]);

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);
  const totalsBg = alpha(themeColor, 0.06);

  const cellPadding = isLandscape ? "4px" : "6px";
  const cell = { border: `1px solid ${borderColor}`, padding: cellPadding };
  const cell1 = { border: `1px solid ${borderColor}`, padding: isLandscape ? "2px 3px" : "0px 2px" };
  const headCell = { ...cell, background: headerBg };

  // Get final record for result
  const finalRecord = findTerm("Final") || marksheets[marksheets.length - 1] || {};

  // Render student info field
  const renderStudentField = (fieldKey) => {
    if (!fieldVisibility[fieldKey]) return null;

    const field = STUDENT_INFO_FIELDS[fieldKey];
    let value = '';

    switch (fieldKey) {
      case 'apaarId':
        value = profile?.apaarId || '';
        break;
      case 'name':
        value = toCamelCase(`${profile?.name || ''} ${profile?.last || ''}`.trim());
        break;
      case 'father':
        value = toCamelCase(`${profile?.father || ''} ${profile?.last || ''}`.trim());
        break;
      case 'mother':
        value = toCamelCase(`${profile?.mother || ''} ${profile?.last || ''}`.trim());
        break;
      case 'studied':
        value = `${classroomInfo?.standard || ''} | ${classroomInfo?.section || ''}`;
        break;
      case 'admissionNo':
        value = profile?.id || profile?.studentId || '';
        break;
      case 'casteCategory':
        value = `${profile?.category || ''} | caste: ${profile?.caste || ''}`;
        break;
      case 'dob':
        value = profile?.birthday ? new Date(profile.birthday).toLocaleDateString("en-GB") : '';
        break;
      case 'dobInWords':
        value = profile?.birthday ? convertDateToWords(profile.birthday) : '';
        break;
      case 'heightWeight':
        return (
          <Box key={fieldKey} display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" component="span" fontWeight={700} sx={{ fontSize: "0.8rem" }}>{field.label}</Typography>
            <TextField
              variant="standard"
              placeholder="Height/Weight"
              size="small"
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
        );
      case 'photo':
        return profile?.photo ? (
          <Box key={fieldKey} display="flex" justifyContent="center" my={0.5}>
            <img
              src={profile.photo}
              alt="Student"
              style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
              onError={(e) => e.target.style.display = 'none'}
            />
          </Box>
        ) : null;
      case 'aadhaarNumber':
        value = profile?.aadhaar || '';
        break;
      case 'contact':
        value = profile?.mobile || '';
        break;
      case 'standard':
        value = `${classroomInfo?.standard || ''} | ${classroomInfo?.section || ''}`;
        break;
      case 'address':
        value = toCamelCase(`${profile?.address || ''} ${profile?.city || ''} ${profile?.state || ''}`.trim()) || '';
        break;
      case 'rollNo':
        value = profile?.rollNumber || '';
        break;
      default:
        return null;
    }

    if (!value) return null;

    return (
      <Box
        key={fieldKey}
        sx={{
          display: "grid",
          gridTemplateColumns: isLandscape ? "180px auto" : "120px auto",
          mb: 0.3,
          gap: 0,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: isLandscape ? "0.75rem" : "0.85rem" }} component="span">
          {field.label} :
        </Typography>
        <Typography sx={{ fontSize: isLandscape ? "0.75rem" : "0.85rem" }} component="span">
          {value}
        </Typography>
      </Box>
    );
  };

  const visibleFields = Object.keys(STUDENT_INFO_FIELDS)
    .filter(key => fieldVisibility[key]);

  const fontSize = isLandscape ? "0.7rem" : "0.85rem";
  const headerFontSize = isLandscape ? "0.75rem" : "0.9rem";

  // Marks Cells component
  const MarksCells = (mark, keyPrefix) =>
    mark?.remark !== "Non" && (
      <React.Fragment key={keyPrefix}>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {mark?.maxMark ?? 100}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {Math.round(((mark?.maxMark || 100) * 34) / 100)}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {mark?.marksObtained ?? "-"}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {mark?.grade ?? "-"}
        </TableCell>
      </React.Fragment>
    );

  const MarksCellsNon = (mark, keyPrefix) =>
    mark?.remark === "Non" && (
      <React.Fragment key={keyPrefix}>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {mark?.maxMark ?? 100}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {Math.round(((mark?.maxMark || 100) * 34) / 100)}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {mark?.marksObtained ?? "-"}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: fontSize }}>
          {mark?.grade ?? "-"}
        </TableCell>
      </React.Fragment>
    );

  // If no marksheets or subjects, show a message
  if (marksheets.length === 0 || subjects.length === 0) {
    return (
      <Box sx={{
        p: 3, textAlign: "center", //border: `2px solid ${borderColor}`,
        borderRadius: "12px", mb: 2,
        "@media print": { display: "none" },
      }}>
        <Typography variant="h6" color="textSecondary">
          No marksheet data available for this student.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Student: {profile?.studentName || profile?.name || 'Unknown'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box display={"flex"} flexDirection={"column"}
        sx={{ maxWidth: isLandscape ? "1100px" : "800px", margin: "auto" }}>
        {/* Page 1 - Marks Table */}
        <Box sx={{
          p: isLandscape ? 1.5 : 2,
          background: "#fff",
          fontFamily: "Arial",
          maxWidth: isLandscape ? "1100px" : "800px",
          border: `2px solid ${borderColor}`,
          borderRadius: "12px",
        }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
              alignItems: "center",
              mb: 0.5,
              mt: 0,
            }}
          >
            <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ color: borderColor, fontSize: headerFontSize }} component="span">
              PROGRESS REPORT SESSION - {classroomInfo?.academicYear || profile?.academicYear || 'N/A'}
            </Typography>
          </Box>

          <Divider sx={{ my: 0.5, borderColor }} />

          {/* Student Info */}
          <Box sx={{ display: "flex", justifyContent: "space-around", mb: 1, flexWrap: "wrap", gap: 0.5 }}>
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Student's Name: </strong> {toCamelCase(profile?.studentName || `${profile?.name || ''} ${profile?.last || ''}`.trim())}
            </Box>
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Standard: </strong>{classroomInfo?.standard || 'N/A'} | {classroomInfo?.section || 'N/A'}
            </Box>
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Roll No.: </strong> {profile?.rollNumber || 'N/A'}
            </Box>
          </Box>

          {/* Marks Table */}
          <Box sx={{ p: 0, mb: 0.5, overflowX: 'auto' }}>
            <Table size={isLandscape ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell} rowSpan={2} align="center">
                    <strong style={{ fontSize: headerFontSize }}>Subject</strong>
                  </TableCell>
                  {visibleTerms.map((term) => (
                    <TableCell key={term.key} sx={headCell} colSpan={4} align="center">
                      <TextField
                        sx={{
                          mt: -0.5,
                          mb: -1,
                          "& .MuiInputBase-input": {
                            fontWeight: 700,
                            fontSize: fontSize,
                            color: borderColor
                          },
                        }}
                        variant="standard"
                        placeholder={term.label}
                        slotProps={{ input: { disableUnderline: true } }}
                      />
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {visibleTerms.flatMap((term) => [
                    <TableCell key={`max-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Max
                    </TableCell>,
                    <TableCell key={`min-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Min
                    </TableCell>,
                    <TableCell key={`obt-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Obt
                    </TableCell>,
                    <TableCell key={`grade-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Grade
                    </TableCell>,
                  ])}
                </TableRow>
              </TableHead>

              <TableBody>
                {subjects.map((sub, i) => (
                  <TableRow key={`subject-${i}`}>
                    {sub.remark !== "Non" && (<>
                      <TableCell sx={cell} style={{ fontSize: fontSize }}>
                        {toCamelCase(sub.subjectName)}
                      </TableCell>
                      {visibleTerms.map((term) =>
                        MarksCells(
                          findMark(term.key, sub.subjectName),
                          `${term.key}-${i}`
                        )
                      )}
                    </>)}
                  </TableRow>
                ))}

                {/* Totals Row */}
                <TableRow sx={{ background: totalsBg }}>
                  <TableCell sx={cell}>
                    <strong style={{ fontSize: headerFontSize }}>Total Mark</strong>
                  </TableCell>

                  {visibleTerms.flatMap((term) => {
                    const t = academicTotals[term.key];
                    return [
                      <TableCell key={`${term.key}-amax`} sx={cell} align="center" style={{ fontSize: fontSize }}>
                        {t.max}
                      </TableCell>,
                      <TableCell key={`${term.key}-amin`} sx={cell} />,
                      <TableCell key={`${term.key}-aobt`} sx={cell} align="center" style={{ fontSize: fontSize }}>
                        {t.obt}
                      </TableCell>,
                      <TableCell key={`${term.key}-agrade`} sx={cell} align="center" style={{ fontSize: fontSize }}>
                        {t.grade}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ color: borderColor, fontSize: headerFontSize }} >
            {"NON ACADEMIC PERFORMANCE"}
          </Typography>

          <Box sx={{ p: 0, mb: 1, overflowX: 'auto' }}>
            <Table size={isLandscape ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell} rowSpan={2} align="center">
                    <strong style={{ fontSize: headerFontSize }}>Subject</strong>
                  </TableCell>

                  {visibleTerms.map((term) => (
                    <TableCell key={term.key} sx={headCell} colSpan={4} align="center">
                      <TextField
                        sx={{
                          mt: -0.5,
                          mb: -1,
                          "& .MuiInputBase-input": {
                            fontWeight: 700,
                            fontSize: fontSize,
                            color: borderColor
                          },
                        }}
                        variant="standard"
                        placeholder={term.label}
                        slotProps={{ input: { disableUnderline: true } }}
                      />
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow>
                  {visibleTerms.flatMap((term) => [
                    <TableCell key={`nmax-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Max
                    </TableCell>,
                    <TableCell key={`nmin-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Min
                    </TableCell>,
                    <TableCell key={`nobt-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Obt
                    </TableCell>,
                    <TableCell key={`ngrade-${term.key}`} sx={headCell} align="center" style={{ fontSize: fontSize }}>
                      Grade
                    </TableCell>,
                  ])}
                </TableRow>
              </TableHead>

              <TableBody>
                {subjects.map((sub, i) => (
                  <TableRow key={`non-subject-${i}`}>
                    {sub.remark === "Non" && (<>
                      <TableCell sx={cell} style={{ fontSize: fontSize }}>
                        {toCamelCase(sub.subjectName)}
                      </TableCell>
                      {visibleTerms.map((term) =>
                        MarksCellsNon(
                          findMark(term.key, sub.subjectName),
                          `${term.key}-non-${i}`
                        )
                      )}
                    </>)}
                  </TableRow>
                ))}

                {/* Totals Row */}
                <TableRow sx={{ background: totalsBg }}>
                  <TableCell sx={cell}>
                    <strong style={{ fontSize: headerFontSize }}>Total Mark</strong>
                  </TableCell>

                  {visibleTerms.flatMap((term) => {
                    const t = nonAcademicTotals[term.key];
                    return [
                      <TableCell key={`${term.key}-nmax`} sx={cell} align="center" style={{ fontSize: fontSize }}>
                        {t.max}
                      </TableCell>,
                      <TableCell key={`${term.key}-nmin`} sx={cell} />,
                      <TableCell key={`${term.key}-nobt`} sx={cell} align="center" style={{ fontSize: fontSize }}>
                        {t.obt}
                      </TableCell>,
                      <TableCell key={`${term.key}-ngrade`} sx={cell} align="center" style={{ fontSize: fontSize }}>
                        {t.grade}
                      </TableCell>,
                    ];
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Result Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              mb: 0.5,
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Result: </strong> {finalRecord?.result || 'N/A'}
            </Box>
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Percentage: </strong> {finalRecord?.percentage || 'N/A'}%
            </Box>
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Grade: </strong> {finalRecord?.grade || 'N/A'}
            </Box>
            <Box component="span" sx={{ fontSize: fontSize }}>
              <strong>Remarks: </strong> {finalRecord?.remarks || 'N/A'}
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
              py: 2,
            }}
          >
            <Box component="span" sx={{ fontSize: fontSize }}>Class Teacher Sign_____________ </Box>
            <Box component="span" sx={{ fontSize: fontSize }}>Head Master _____________</Box>
          </Box>
        </Box>

        <Divider className="pagebreak" />

        {/* Page 2 - Student Profile & Activities */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isLandscape ? "row" : "row",
            justifyContent: "space-around",
            alignItems: "stretch",
            border: `2px solid ${borderColor}`,
            borderRadius: "12px",
            backgroundColor: "#fff",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          {/* Left Column - Student Profile */}
          <Box sx={{ flex: isLandscape ? 1 : 1, minWidth: isLandscape ? "280px" : "280px", p: isLandscape ? 1 : 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                alignItems: "center",
                mb: 0.5,
                mt: 0,
              }}
            >
              <img
                src={school?.logoUrl}
                alt="School Logo"
                style={{ width: isLandscape ? 60 : 80, height: isLandscape ? 50 : 70, marginTop: 5 }}
                onError={(e) => e.target.style.display = 'none'}
              />

              <Typography variant={isLandscape ? "h6" : "h6"} align="center" fontWeight="bold" sx={{ color: borderColor }} component="span">
                {school?.schoolName}
              </Typography>
              <Typography variant="subtitle2" align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.8rem" }} component="span">
                {toCamelCase(
                  [
                    school?.addressDTO?.addressLine1,
                    school?.addressDTO?.city,
                    school?.addressDTO?.pincode,
                    school?.addressDTO?.state,
                  ].filter(Boolean).join(", ")
                )}
              </Typography>
              <Typography variant="subtitle1" align="center" fontWeight="bold" style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }} component="span">
                PROGRESS REPORT SESSION - {classroomInfo?.academicYear || profile?.academicYear || 'N/A'}
              </Typography>

              {/* Photo */}
              {fieldVisibility.photo && profile?.photo && (
                <img
                  src={profile.photo}
                  alt="Student"
                  style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, marginTop: 5, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </Box>

            <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ bgcolor: headerBg, py: 0.3, borderRadius: 1, fontSize: isLandscape ? "0.75rem" : "0.9rem" }} >
              {"STUDENT PROFILE"}
            </Typography>
            <Divider sx={{ mb: 1, borderColor }} />

            {/* Student Info */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                px: 1,
              }}
            >
              {visibleFields
                .filter(key => key !== 'photo')
                .map(key => renderStudentField(key))}
            </Box>
          </Box>

          <Divider orientation={isLandscape ? "vertical" : "vertical"} flexItem sx={{ borderColor }} />

          {/* Right Column - Activities */}
          <Box sx={{ flex: isLandscape ? 1.2 : 1, minWidth: isLandscape ? "360px" : "360px", p: isLandscape ? 1 : 1 }}>
            {/* Activity Table */}
            <Box sx={{ mb: 1, overflowX: 'auto' }}>
              <Table size={isLandscape ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={cell1} colSpan={activities.length + 1} align="center">
                      <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Co-Curricular Activity</strong>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={cell1} align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                      {"#"}
                    </TableCell>
                    {activities.map((a, i) => (
                      <TableCell sx={cell1} key={`activity-${i}`} align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                        {a.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {totalActivity.map((sub, i) => {
                    return (
                      <TableRow key={`total-activity-${i}`}>
                        <TableCell sx={cell1} style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                          {toCamelCase(sub.name)}
                        </TableCell>
                        {activities.map((_, j) => (
                          <TableCell sx={cell1} align="center" key={`act-${i}-${j}`}>
                            <TextField
                              variant="standard"
                              placeholder="Very Good"
                              slotProps={{ input: { disableUnderline: true } }}
                              sx={{
                                "& input": {
                                  fontFamily: "inherit",
                                  fontSize: isLandscape ? "0.6rem" : "0.75rem",
                                  padding: "2px 4px",
                                },
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Total Grade Table */}
            <Box sx={{ mb: 1 }}>
              <Table size={isLandscape ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={cell1} colSpan={totalGrade.length + 1} align="center">
                      <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Total Grade</strong>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    {totalGrade.map((a, j) => (
                      <TableCell sx={cell1} key={`grade-label-${j}`} align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                        {a.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {subjects.map((sub, i) => {
                    return (
                      <TableRow key={`grade-row-${i}`}>
                        {i < 3 &&
                          totalGrade.map((_, j) => (
                            <TableCell key={`grade-cell-${i}-${j}`} sx={cell1}>
                              {j === 0 ? (
                                terms[i]?.n || "-"
                              ) : (
                                <TextField
                                  variant="standard"
                                  placeholder=".........."
                                  slotProps={{ input: { disableUnderline: true } }}
                                  sx={{
                                    "& input": {
                                      fontSize: isLandscape ? "0.6rem" : "0.75rem",
                                      padding: "2px 4px",
                                    },
                                  }}
                                />
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <TextField
              sx={{
                mt: 0.5,
                width: '100%',
                "& .MuiInputBase-input": { fontSize: isLandscape ? "0.7rem" : "0.85rem" },
              }}
              variant="standard"
              placeholder="Very Good"
              slotProps={{ input: { disableUnderline: true } }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-around",
                my: 1,
              }}
            >
              <Box component="span" sx={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Class Teacher sign </Box>
              <Box component="span" sx={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Head Master </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* page break between students (not after the very last one) */}
      {!isLast && <Divider className="pagebreak" />}
    </>
  );
}

export default function TwoPageMarksheetReceiptBulk({ receiptData, school, setTemID }) {
  const activities = [
    { name: "Formative Assessment-I" },
    { name: "Formative Assessment-II" },
    { name: "Final-Exam" },
  ];

  const totalGrade = [
    { name: "Semester" },
    { name: "Education" },
    { name: "Co-Curricular" },
    { name: "Total" },
    { name: "Special Remark" },
    { name: "C. Teacher Signature" },
  ];

  const totalActivity = [
    { name: "Qualities" },
    { name: "Creativity" },
    { name: "Regularity" },
    { name: "Punctuality" },
    { name: "Cleanness" },
    { name: "Cultural" },
    { name: "Discipline" },
    { name: "Confidence" },
    { name: "School Program" },
    { name: "School Mates " },
  ];

  const [terms] = useState([{ n: "First" }, { n: "Second" }, { n: "Final" }]);

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#000000");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [showSettings, setShowSettings] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('bulkMarksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  // receiptData.data is now an array of student objects
  const students = receiptData.data || [];
  const receiptId = `marksheet`;

  // console.log("Students data:", students);

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('bulkMarksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // Add landscape styles to document
  useEffect(() => {
    if (isLandscape) {
      const style = document.createElement('style');
      style.id = 'landscape-style-bulk';
      style.innerHTML = `
        @media print {
          @page {
            size: landscape;
            margin: 8mm;
          }
          .pagebreak {
            page-break-after: always;
          }
        }
      `;
      document.head.appendChild(style);
    } else {
      const existingStyle = document.getElementById('landscape-style-bulk');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
    return () => {
      const existingStyle = document.getElementById('landscape-style-bulk');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLandscape]);

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  // Toggle landscape mode
  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  const containerMaxWidth = isLandscape ? "1100px" : "800px";

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: containerMaxWidth,
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem" component="span">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="term-filter-label" sx={{ fontSize: "0.8rem" }}>Term</InputLabel>
          <Select
            labelId="term-filter-label"
            label="Term"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            sx={{ fontSize: "0.8rem" }}
          >
            <MenuItem value="all">All Terms</MenuItem>
            {TERMS.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
          <Chip
            size="small"
            label={`${visibleCount}/${totalFields}`}
            color="primary"
            variant="outlined"
            sx={{ height: 28 }}
          />
        </Tooltip>

      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: containerMaxWidth,
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem" component="span">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)} sx={{ fontSize: "0.75rem" }}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)} sx={{ fontSize: "0.75rem" }}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={0.5}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5} component="span">
                      <Typography variant="caption" fontSize="0.7rem" component="span">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Render each student's marksheet */}
      {students.map((student, idx) => {
        // console.log(`Rendering student ${idx}:`, student);
        return (
          <StudentTwoPageMarksheet
            key={student?.id ?? student?.studentId ?? idx}
            studentData={student}
            school={school}
            activities={activities}
            totalGrade={totalGrade}
            totalActivity={totalActivity}
            terms={terms}
            isLast={idx === students.length - 1}
            themeColor={themeColor}
            selectedTerm={selectedTerm}
            isLandscape={isLandscape}
            fieldVisibility={fieldVisibility}
          />
        );
      })}

      {/* Empty state */}
      {students.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="textSecondary">
            No student data available.
          </Typography>
        </Box>
      )}
    </Box>
  );
}